/**
 * Le brouillon de facturation de la garderie, côté écran : la forme du document
 * que rend `garderie_brouillon(centre, mois)` (migration
 * `20261101090000_le_brouillon_de_la_garderie`), et les aides pures qui vont
 * avec — les jours d'école du midi proposés pour une période, la période par
 * défaut d'un mois, les mois d'une année scolaire, l'export CSV, les libellés.
 *
 * Le calcul lui-même est dans la base (jumeau SQL de `calcul.ts`) : ce module
 * ne calcule aucun montant, il présente. PROPOSITION TECHNIQUE. [cdv:garderie]
 */

export type TypeDeLigneDuBrouillon = 'formule' | 'plage' | 'midi';

export type CodeAvertissement = 'payeurs-invalides' | 'formule-inconnue' | 'plage-inconnue' | 'presence-hors-jours' | 'sorti';

export interface LigneDuBrouillon {
  type: TypeDeLigneDuBrouillon;
  code: string;
  libelle: string;
  quantite: number;
  prix_unitaire_cents: number;
  montant_total_cents: number;
  part_pct: number;
  montant_cents: number;
  /** `AAAA-MM-JJ`, les dates comptées d'une ligne de plage. */
  dates: string[];
  a_confirmer: boolean;
  detail: string | null;
}

export interface EnfantDuBrouillon {
  enfant_id: string;
  nom: string;
  prenom: string;
  total_cents: number;
  a_confirmer_cents: number;
  lignes: LigneDuBrouillon[];
}

export interface AvertissementDuBrouillon {
  enfant_id: string;
  enfant: string;
  code: CodeAvertissement | string;
  libelle: string;
  detail: string | null;
  dates: string[];
}

export interface FamilleDuBrouillon {
  famille_id: string;
  nom: string;
  prenom: string | null;
  email: string | null;
  tiers_id: string | null;
  statut: 'plein' | 'reduit' | 'professionnel';
  total_cents: number;
  a_confirmer_cents: number;
  enfants: EnfantDuBrouillon[];
  avertissements: AvertissementDuBrouillon[];
}

export interface MoisDeGarderie {
  id?: string;
  center_id: string;
  /** `AAAA-MM-01`. */
  mois: string;
  periode_du: string;
  periode_au: string;
  jours_ecole_midi: number;
  offert_du: string | null;
  offert_au: string | null;
  etat: 'ouvert' | 'brouillon' | 'facture';
  facture_le?: string | null;
  notes?: string | null;
}

/** Ce que rend `garderie_emettre_mois` (migration `20261101220000_l_emission_des_factures_de_la_garderie`). */
export interface ResultatDeLEmission {
  center_id: string;
  mois: string;
  factures: number;
  montant_cents: number;
  a_confirmer_cents: number;
  sans_email: number;
  avec_annotation: number;
  avertissements: number;
  premier_numero: string | null;
  dernier_numero: string | null;
}

/** Une facture émise d'un mois, telle que l'écran la liste (`garderie_factures` + `invoices`). */
export interface FactureEmise {
  id: string;
  famille_id: string;
  nom: string;
  prenom: string | null;
  email: string | null;
  statut: 'plein' | 'reduit' | 'professionnel';
  /** Les douze chiffres. */
  communication: string;
  montant_cents: number;
  a_confirmer_cents: number;
  sans_abonnement: boolean;
  annotation: string | null;
  emise_le: string;
  numero: string;
  /** `+++509/2602/58354+++`. */
  communication_imprimee: string;
  echeance: string | null;
  statut_facture: string;
  pdf_url: string | null;
  courriel_envoye_le: string | null;
  enfants: number;
  lignes: number;
}

/**
 * L'annotation portée par une facture dont un enfant est facturé sans abonnement
 * (VALIDÉ ÉCLOSION 01/11/2026 : documents d'inscription non reçus, régularisation aux
 * permanences). Même texte que le défaut de la base ; l'administration peut l'adapter
 * avant d'émettre.
 */
export const ANNOTATION_PAR_DEFAUT =
  'Nous n’avons pas reçu les documents d’inscription à la garderie en début d’année : les présences de cette facture sont comptées à la carte. '
  + 'Vous pouvez encore vous mettre en ordre en passant aux permanences de l’école.';

export interface Brouillon {
  mois: MoisDeGarderie;
  ecole: { id: string; nom: string; tag: string };
  annee_scolaire: number;
  prix_midi_cents: number;
  familles: FamilleDuBrouillon[];
  totaux: {
    familles: number;
    enfants: number;
    lignes: number;
    total_cents: number;
    a_confirmer_cents: number;
    par_type: Partial<Record<TypeDeLigneDuBrouillon, number>>;
    par_code: { type: TypeDeLigneDuBrouillon; code: string; enfants: number; montant_cents: number }[];
    avertissements: number;
  };
  avertissements: AvertissementDuBrouillon[];
}

export const LIBELLES_AVERTISSEMENTS: Record<CodeAvertissement, string> = {
  'payeurs-invalides': 'Parts des payeurs qui ne font pas 100 : rien n’est facturé',
  'formule-inconnue': 'Abonné à une formule hors du catalogue de l’année',
  'plage-inconnue': 'Présence sur une plage hors du catalogue, non facturée',
  'presence-hors-jours': 'Présence un jour où la plage ne tourne pas, comptée',
  sorti: 'Sorti de l’école mais encore abonné ou pointé : facturé',
};

export const LIBELLES_TYPES: Record<TypeDeLigneDuBrouillon, string> = {
  formule: 'Formules',
  plage: 'À la carte (présences)',
  midi: 'Temps de midi à la carte',
};

/* ------------------------------------------------------------------------- */
/* Le calendrier                                                              */
/* ------------------------------------------------------------------------- */

const jourUtc = (iso: string): Date => {
  const [a, m, j] = iso.split('-').map(Number);
  return new Date(Date.UTC(a, m - 1, j));
};
const enIso = (d: Date): string => d.toISOString().slice(0, 10);

/**
 * Les jours d'école du temps de midi dans une période : lundi, mardi, jeudi,
 * vendredi (le mercredi se termine à midi), moins les jours exclus (congés,
 * fériés), bornes comprises. L'administration corrige le nombre à la main si
 * l'école a eu une journée pédagogique.
 */
export function compterLesJoursDeMidi(du: string, au: string, exclusions: readonly { du: string; au: string }[] = []): number {
  if (!du || !au || du > au) return 0;
  let n = 0;
  for (let d = jourUtc(du); enIso(d) <= au; d.setUTCDate(d.getUTCDate() + 1)) {
    const iso = enIso(d);
    const jour = d.getUTCDay(); // 0 dimanche … 6 samedi
    if (jour === 0 || jour === 3 || jour === 6) continue;
    if (exclusions.some((x) => iso >= x.du && iso <= x.au)) continue;
    n++;
  }
  return n;
}

/** Le dernier jour d'un mois `AAAA-MM-01`. */
export function finDuMois(mois: string): string {
  const [a, m] = mois.split('-').map(Number);
  return enIso(new Date(Date.UTC(a, m, 0)));
}

/**
 * La période par défaut d'un mois : le mois civil, sauf le premier mois de
 * l'année scolaire, qui commence au premier jour de l'année (la semaine d'août
 * dans la facture de septembre).
 */
export function periodeParDefaut(mois: string, debutAnnee: string, finAnnee: string): { du: string; au: string } {
  const fin = finDuMois(mois);
  const du = debutAnnee > mois && debutAnnee <= fin ? mois : debutAnnee < mois && debutAnnee > mois.slice(0, 7) + '-01' ? mois : mois;
  const premierMois = debutAnnee.slice(0, 7) === mois.slice(0, 7);
  const moisSuivantLeDebut = debutAnnee < mois && debutAnnee.slice(0, 7) !== mois.slice(0, 7) && estLeMoisSuivant(debutAnnee, mois);
  return {
    du: premierMois ? debutAnnee : moisSuivantLeDebut ? debutAnnee : du,
    au: finAnnee < fin ? finAnnee : fin,
  };
}

/** `debut` tombe dans le mois qui précède `mois` (le 24 août pour septembre). */
function estLeMoisSuivant(debut: string, mois: string): boolean {
  const [a, m] = mois.split('-').map(Number);
  const precedent = new Date(Date.UTC(a, m - 2, 1));
  return debut.slice(0, 7) === enIso(precedent).slice(0, 7);
}

/** Les mois d'une année scolaire, de septembre à juin, en `AAAA-MM-01`. */
export function moisDeLAnnee(anneeScolaire: number): string[] {
  const mois: string[] = [];
  for (let i = 0; i < 10; i++) {
    const d = new Date(Date.UTC(anneeScolaire, 8 + i, 1));
    mois.push(enIso(d));
  }
  return mois;
}

const NOMS_DES_MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

export function moisEnClair(mois: string): string {
  const [a, m] = mois.split('-').map(Number);
  return `${NOMS_DES_MOIS[m - 1]} ${a}`;
}

export function dateEnClair(iso: string): string {
  const [a, m, j] = iso.split('-');
  return `${j}/${m}/${a}`;
}

/* ------------------------------------------------------------------------- */
/* Les montants et l'export                                                   */
/* ------------------------------------------------------------------------- */

const formateur = new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' });

export function enEuros(cents: number): string {
  return formateur.format(cents / 100);
}

/** `4400` → `44,00`, pour un tableur. */
export function enDecimal(cents: number): string {
  const signe = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  return `${signe}${Math.floor(abs / 100)},${String(abs % 100).padStart(2, '0')}`;
}

const cellule = (v: string | number | boolean | null | undefined): string => {
  const s = v === null || v === undefined ? '' : typeof v === 'boolean' ? (v ? 'oui' : 'non') : String(v);
  return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** Le brouillon en CSV (point-virgule, pour un tableur francophone) : une ligne par ligne de facture. */
export function brouillonEnCsv(b: Brouillon): string {
  const entete = ['École', 'Mois', 'Famille', 'Prénom', 'E-mail', 'Statut', 'Enfant', 'Type', 'Code', 'Libellé', 'Quantité', 'Prix unitaire', 'Total de la ligne', 'Part %', 'Montant', 'À confirmer', 'Dates'];
  const lignes: string[] = [entete.join(';')];
  for (const f of b.familles) {
    for (const e of f.enfants) {
      for (const l of e.lignes) {
        lignes.push(
          [
            b.ecole.tag,
            b.mois.mois.slice(0, 7),
            f.nom,
            f.prenom,
            f.email,
            f.statut,
            `${e.nom} ${e.prenom}`,
            l.type,
            l.code,
            l.libelle,
            l.quantite,
            enDecimal(l.prix_unitaire_cents),
            enDecimal(l.montant_total_cents),
            l.part_pct,
            enDecimal(l.montant_cents),
            l.a_confirmer,
            l.dates.map(dateEnClair).join(' '),
          ]
            .map(cellule)
            .join(';'),
        );
      }
    }
  }
  return lignes.join('\n') + '\n';
}

/** Les avertissements comptés par code, dans l'ordre des libellés connus puis le reste. */
export function compterLesAvertissements(b: Brouillon): { code: string; libelle: string; nombre: number }[] {
  const compte = new Map<string, number>();
  for (const a of b.avertissements) compte.set(a.code, (compte.get(a.code) ?? 0) + 1);
  return [...compte.entries()]
    .map(([code, nombre]) => ({ code, libelle: (LIBELLES_AVERTISSEMENTS as Record<string, string>)[code] ?? code, nombre }))
    .sort((a, b) => b.nombre - a.nombre || a.code.localeCompare(b.code));
}

/** Les familles qui ont quelque chose à confirmer ou un avertissement, pour le filtre de l'écran. */
export function famillesAVoir(b: Brouillon): FamilleDuBrouillon[] {
  return b.familles.filter((f) => f.a_confirmer_cents > 0 || f.avertissements.length > 0);
}

export function chercherLesFamilles(familles: readonly FamilleDuBrouillon[], texte: string): FamilleDuBrouillon[] {
  const t = texte
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
  if (!t) return [...familles];
  const norm = (s: string | null) => (s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  return familles.filter((f) => norm(f.nom).includes(t) || norm(f.prenom).includes(t) || norm(f.email).includes(t) || f.enfants.some((e) => norm(`${e.nom} ${e.prenom}`).includes(t)));
}

/* ------------------------------------------------------------------------- */
/* L'émission                                                                 */
/* ------------------------------------------------------------------------- */

/** Les enfants dont le midi est « à confirmer » (sans formule ni présence), avec leur famille, pour régler la fiche avant d'émettre. */
export function enfantsAConfirmer(b: Brouillon): { enfant_id: string; enfant: string; famille: string; montant_cents: number }[] {
  const vus = new Map<string, { enfant_id: string; enfant: string; famille: string; montant_cents: number }>();
  for (const f of b.familles) {
    for (const e of f.enfants) {
      const montant = e.lignes.filter((l) => l.a_confirmer).reduce((n, l) => n + l.montant_cents, 0);
      if (montant <= 0) continue;
      const deja = vus.get(e.enfant_id);
      if (deja) {
        deja.montant_cents += montant;
        deja.famille += `, ${f.nom}`;
      } else {
        vus.set(e.enfant_id, { enfant_id: e.enfant_id, enfant: `${e.nom} ${e.prenom}`, famille: f.nom, montant_cents: montant });
      }
    }
  }
  return [...vus.values()].sort((a, b) => a.enfant.localeCompare(b.enfant, 'fr'));
}

/** Les familles du brouillon qui recevront une facture : total strictement positif. */
export function famillesFacturables(b: Brouillon): FamilleDuBrouillon[] {
  return b.familles.filter((f) => f.total_cents > 0);
}

/** Les factures émises d'un mois en CSV (point-virgule) : une ligne par facture. */
export function facturesEnCsv(factures: readonly FactureEmise[], ecole: string, mois: string): string {
  const entete = ['École', 'Mois', 'Numéro', 'Famille', 'Prénom', 'E-mail', 'Statut', 'Montant', 'À confirmer', 'Communication', 'Échéance', 'Sans abonnement', 'Enfants', 'Lignes', 'PDF', 'Courriel envoyé le'];
  const lignes = factures.map((f) =>
    [
      ecole,
      mois.slice(0, 7),
      f.numero,
      f.nom,
      f.prenom,
      f.email,
      f.statut,
      enDecimal(f.montant_cents),
      enDecimal(f.a_confirmer_cents),
      f.communication_imprimee,
      f.echeance ? dateEnClair(f.echeance.slice(0, 10)) : '',
      f.sans_abonnement,
      f.enfants,
      f.lignes,
      f.pdf_url ? 'oui' : 'non',
      f.courriel_envoye_le ? dateEnClair(f.courriel_envoye_le.slice(0, 10)) : '',
    ]
      .map(cellule)
      .join(';'),
  );
  return [entete.join(';'), ...lignes].join('\n') + '\n';
}

/** Le total des factures listées. */
export function totalDesFactures(factures: readonly FactureEmise[]): { nombre: number; montant_cents: number; sans_email: number; sans_abonnement: number } {
  return {
    nombre: factures.length,
    montant_cents: factures.reduce((n, f) => n + f.montant_cents, 0),
    sans_email: factures.filter((f) => !f.email).length,
    sans_abonnement: factures.filter((f) => f.sans_abonnement).length,
  };
}

/** Le message d'une erreur de la base sans son étiquette `[…]`, pour l'écran. */
export function messageDeLaBase(e: unknown): string {
  const brut = e instanceof Error ? e.message : typeof e === 'object' && e !== null && 'message' in e ? String((e as { message: unknown }).message) : String(e);
  return brut.replace(/^\[[a-z-]+\]\s*/i, '');
}
